import {
    IonButton,
    IonIcon,
    IonProgressBar
} from "@ionic/react";

import {
    chevronForwardOutline,
    peopleOutline,
    statsChartOutline
} from "ionicons/icons";

import {
    useEffect,
    useMemo,
    useState
} from "react";

import { useHistory } from "react-router-dom";

import {
    CompetitionGame,
    GameDetail,
    GamePlay,
    PlayersById,
    getCompetitionDetail,
    getGameDetail,
    getPlayerDetail
} from "../services/mexfbaApi";

// Ajusta esta ruta si StatsFunction está en otra carpeta.
import * as Components from "../pages/StatsFunction";

interface GameHighlightsProps {
    detail: GameDetail;
    players: PlayersById;
    refreshToken: number;
}

interface PlayerStat {
    id: string;
    [key: string]: string | number;
}

interface LeaderBuckets {
    quarterbacks: PlayerStat[];
    runners: PlayerStat[];
    receivers: PlayerStat[];
}

interface BothTeamsLeaders {
    local: LeaderBuckets;
    visitor: LeaderBuckets;
}

interface QuickTeamStats {
    id: string;
    pass: number;
    rush: number;
    int: number;
    fr: number;

    // StatsFunction agrega otras propiedades al crear el objeto.
    [key: string]: string | number;
}

type LeaderType = "QB" | "RB" | "WR";

function getNumber(value: unknown): number {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
}

function emptyLeaders(): LeaderBuckets {
    return {
        quarterbacks: [],
        runners: [],
        receivers: []
    };
}

function emptyBothTeams(): BothTeamsLeaders {
    return {
        local: emptyLeaders(),
        visitor: emptyLeaders()
    };
}

function getPlays(detail: GameDetail): GamePlay[] {
    if (!detail.Jugadas) {
        return [];
    }

    return Array.isArray(detail.Jugadas)
        ? detail.Jugadas
        : Object.values(detail.Jugadas);
}

/*
    Las fechas de los JSON se comparan por día de calendario,
    como en la versión web.
*/
function getDateKey(value?: string): string | null {
    const text = String(value || "").trim();

    const match = text.match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/
    );

    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null;
    }

    return [
        String(year).padStart(4, "0"),
        String(month).padStart(2, "0"),
        String(day).padStart(2, "0")
    ].join("-");
}

/*
    Para comparar con las fechas guardadas en los JSON,
    se usa la misma zona de origen del calendario.
*/
function getTodayInScheduleTimeZone(): string {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Monterrey",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });

    const parts = formatter.formatToParts(new Date());

    const getPart = (type: string): string =>
        parts.find(part => part.type === type)?.value || "";

    return [
        getPart("year"),
        getPart("month"),
        getPart("day")
    ].join("-");
}

function hasValidScore(value: unknown): boolean {
    if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
    ) {
        return false;
    }

    const number = Number(value);

    return Number.isInteger(number) && number >= 0;
}

/*
    Utiliza las mismas funciones de GamePlayers.

    Se mantienen arreglos independientes por equipo para evitar
    mezclar estadísticas de jugadores que hayan cambiado de equipo.
*/
function addPlaysToLeaders(
    plays: GamePlay[],
    localTeamId: string,
    visitorTeamId: string,
    totals: BothTeamsLeaders
): void {
    plays.forEach(function (play) {
        let teamLeaders: LeaderBuckets | null = null;

        if (String(play.ofensiva) === String(localTeamId)) {
            teamLeaders = totals.local;
        } else if (
            String(play.ofensiva) === String(visitorTeamId)
        ) {
            teamLeaders = totals.visitor;
        }

        if (!teamLeaders) {
            return;
        }

        Components.getQB(play, teamLeaders.quarterbacks);
        Components.getRB(play, teamLeaders.runners);
        Components.getWR(play, teamLeaders.receivers);
    });
}

/*
    Igual que GamePlayers:
    - QB: debe tener intentos de pase.
    - RB: debe tener intentos de acarreo.
    - WR: debe tener al menos un balón dirigido.

    Se ordena por yardas de cada categoría.
*/
function findLeader(
    leaders: LeaderBuckets,
    type: LeaderType
): PlayerStat | null {
    let candidates: PlayerStat[] = [];
    let yardsKey = "";

    if (type === "QB") {
        candidates = leaders.quarterbacks.filter(
            player => getNumber(player.att) > 0
        );

        yardsKey = "ydsPas";
    } else if (type === "RB") {
        candidates = leaders.runners.filter(
            player => getNumber(player.attA) > 0
        );

        yardsKey = "ydsAca";
    } else {
        candidates = leaders.receivers.filter(
            player => getNumber(player.attR) > 0
        );

        yardsKey = "ydsRec";
    }

    return [...candidates].sort(function (first, second) {
        return (
            getNumber(second[yardsKey]) -
            getNumber(first[yardsKey])
        );
    })[0] || null;
}

function getLeaderStats(
    player: PlayerStat,
    type: LeaderType
): string[] {
    if (type === "QB") {
        return [
            `${getNumber(player.comp)}/${getNumber(player.att)} CMP/ATT`,
            `${getNumber(player.ydsPas)} YDS`,
            `${getNumber(player.tdsPas)} TD`
        ];
    }

    if (type === "RB") {
        return [
            `${getNumber(player.attA)} ATT`,
            `${getNumber(player.ydsAca)} YDS`,
            `${getNumber(player.tdsAca)} TD`
        ];
    }

    return [
        `${getNumber(player.compR)} REC`,
        `${getNumber(player.ydsRec)} YDS`,
        `${getNumber(player.tdsRec)} TD`
    ];
}

function calculateQuickTeamStats(
    plays: GamePlay[],
    teamId: string
): QuickTeamStats {
    const stats: QuickTeamStats[] = [];

    Components.setStatsTeamAlt(teamId, stats);

    plays.forEach(function (play) {
        if (play.ofensiva === teamId) {
            Components.getOfence(play, stats);
        } else {
            // Igual que el cálculo de GameStats.
            Components.getDefencee(play, stats, teamId);
        }
    });

    return stats[0];
}

function formatQuickValue(value: unknown): string {
    const number = Number(value);

    return Number.isFinite(number)
        ? String(number)
        : "—";
}

/*
    Obtener resultados previos sin presentar como finalizado un
    partido del día actual solo porque ya tenga marcador.
*/
async function loadSeasonLeaders(
    game: CompetitionGame,
    forceRefresh: boolean
): Promise<BothTeamsLeaders> {
    const cutoffDate = getDateKey(game.fecha);

    if (!cutoffDate) {
        throw new Error(
            "El partido no tiene una fecha válida para calcular líderes de temporada."
        );
    }

    if (!game.id_comp) {
        throw new Error(
            "El partido no tiene una competencia identificada."
        );
    }

    const today = getTodayInScheduleTimeZone();

    const competition = await getCompetitionDetail(
        game.id_comp,
        forceRefresh
    );

    const candidates = Object.entries(
        competition.Partidos || {}
    )
        .map(function ([key, item]) {
            return {
                ...item,
                id: String(item.id || key)
            };
        })
        .filter(function (item) {
            const date = getDateKey(item.fecha);

            if (!date) {
                return false;
            }

            // Mismo corte por día que la web.
            if (date >= cutoffDate) {
                return false;
            }

            // No asumir que los partidos del día actual ya terminaron.
            if (date >= today) {
                return false;
            }

            if (String(item.id) === String(game.id)) {
                return false;
            }

            if (
                item.id_comp &&
                String(item.id_comp) !== String(game.id_comp)
            ) {
                return false;
            }

            const local = String(item.id_local);
            const visitor = String(item.id_visita);

            return (
                local === String(game.id_local) ||
                visitor === String(game.id_local) ||
                local === String(game.id_visita) ||
                visitor === String(game.id_visita)
            );
        });

    // Evitar descargar dos veces el mismo partido.
    const uniqueIds = Array.from(
        new Set(candidates.map(item => String(item.id)))
    );

    const totals = emptyBothTeams();

    /*
        Descargar por lotes para no lanzar demasiadas solicitudes
        simultáneas en dispositivos móviles.
    */
    const batchSize = 6;

    for (
        let index = 0;
        index < uniqueIds.length;
        index += batchSize
    ) {
        const batch = uniqueIds.slice(index, index + batchSize);

        const details = await Promise.all(
            batch.map(id =>
                getGameDetail(id, forceRefresh)
            )
        );

        details.forEach(function (detail) {
            const previousGame = detail.Data;
            const date = getDateKey(previousGame.fecha);

            if (
                !date ||
                date >= cutoffDate ||
                date >= today
            ) {
                return;
            }

            if (
                String(previousGame.id) === String(game.id) ||
                String(previousGame.id_comp) !== String(game.id_comp)
            ) {
                return;
            }

            // Se conserva la fase usada por tu web para líderes.
            if (previousGame.partido !== "grupos") {
                return;
            }

            if (
                !hasValidScore(previousGame.resLocal) ||
                !hasValidScore(previousGame.resVisita)
            ) {
                return;
            }

            addPlaysToLeaders(
                getPlays(detail),
                game.id_local,
                game.id_visita,
                totals
            );
        });
    }

    return totals;
}

interface LeaderPlayerProps {
    player: PlayerStat | null;
    type: LeaderType;
    players: PlayersById;
    photos: Record<string, string>;
}

const LeaderPlayer: React.FC<LeaderPlayerProps> = ({
    player,
    type,
    players,
    photos
}) => {
    const [imageFailed, setImageFailed] = useState(false);

    const playerId = player ? String(player.id) : "";
    const playerInfo = playerId ? players[playerId] : undefined;

    const name = playerInfo?.nombre || "Jugador sin registrar";
    const image = playerInfo?.img || photos[playerId] || "";

    useEffect(function () {
        setImageFailed(false);
    }, [image]);

    if (!player) {
        return (
            <div className="game-leaders__player game-leaders__player--empty">
                <span className="game-leaders__avatar-placeholder">
                    —
                </span>

                <strong>Sin registros</strong>
            </div>
        );
    }

    return (
        <div className="game-leaders__player">
            {image && !imageFailed ? (
                <img
                    className="game-leaders__avatar"
                    src={image}
                    alt={`Foto de ${name}`}
                    onError={function () {
                        setImageFailed(true);
                    }}
                />
            ) : (
                <span className="game-leaders__avatar-placeholder">
                    {name.trim().charAt(0).toUpperCase() || "?"}
                </span>
            )}

            <strong>{name}</strong>

            <div className="game-leaders__stats">
                {getLeaderStats(player, type).map(function (stat) {
                    return <span key={stat}>{stat}</span>;
                })}
            </div>
        </div>
    );
};

interface QuickRowProps {
    label: string;
    localValue: number;
    visitorValue: number;
}

const QuickRow: React.FC<QuickRowProps> = ({
    label,
    localValue,
    visitorValue
}) => {
    const localValid = Number.isFinite(localValue);
    const visitorValid = Number.isFinite(visitorValue);

    const localBar = localValid ? Math.max(0, localValue) : 0;
    const visitorBar = visitorValid ? Math.max(0, visitorValue) : 0;

    const maximum = Math.max(localBar, visitorBar, 1);

    return (
        <div className="game-quick-stats__row">
            <div className="game-quick-stats__numbers">
                <strong>{formatQuickValue(localValue)}</strong>
                <span>{label}</span>
                <strong>{formatQuickValue(visitorValue)}</strong>
            </div>

            <div className="game-quick-stats__bars">
                <div className="game-quick-stats__bar game-quick-stats__bar--local">
                    <span
                        style={{
                            width: `${(localBar / maximum) * 100}%`
                        }}
                    />
                </div>

                <div className="game-quick-stats__bar game-quick-stats__bar--visitor">
                    <span
                        style={{
                            width: `${(visitorBar / maximum) * 100}%`
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const GameHighlights: React.FC<GameHighlightsProps> = ({
    detail,
    players,
    refreshToken
}) => {
    const history = useHistory();

    const game = detail.Data;

    const plays = useMemo(function () {
        return getPlays(detail);
    }, [detail]);

    /*
        Si hay jugadas registradas, estas se toman como evidencia
        de que ya existen datos del partido para procesar.
    */
    const hasGamePlays = plays.length > 0;

    const gameLeaders = useMemo(function () {
        if (!hasGamePlays) {
            return null;
        }

        const result = emptyBothTeams();

        addPlaysToLeaders(
            plays,
            game.id_local,
            game.id_visita,
            result
        );

        return result;
    }, [
        plays,
        hasGamePlays,
        game.id_local,
        game.id_visita
    ]);

    const [seasonLeaders, setSeasonLeaders] =
        useState<BothTeamsLeaders | null>(null);

    const [seasonLoading, setSeasonLoading] = useState(false);
    const [seasonError, setSeasonError] = useState("");

    useEffect(function () {
        let active = true;

        if (hasGamePlays) {
            setSeasonLeaders(null);
            setSeasonLoading(false);
            setSeasonError("");

            return function () {
                active = false;
            };
        }

        setSeasonLeaders(null);
        setSeasonLoading(true);
        setSeasonError("");

        loadSeasonLeaders(game, true)
            .then(function (result) {
                if (active) {
                    setSeasonLeaders(result);
                }
            })
            .catch(function (error: unknown) {
                console.error(
                    "No fue posible cargar líderes de temporada:",
                    error
                );

                if (active) {
                    setSeasonError(
                        "No fue posible cargar los líderes de temporada."
                    );
                }
            })
            .finally(function () {
                if (active) {
                    setSeasonLoading(false);
                }
            });

        return function () {
            active = false;
        };
    }, [
        hasGamePlays,
        game.id,
        game.id_comp,
        game.fecha,
        game.id_local,
        game.id_visita,
        refreshToken
    ]);

    const leaders = hasGamePlays
        ? gameLeaders
        : seasonLeaders;

    const rows: {
        type: LeaderType;
        label: string;
        local: PlayerStat | null;
        visitor: PlayerStat | null;
    }[] = leaders
        ? [
            {
                type: "QB",
                label: "Pasador",
                local: findLeader(leaders.local, "QB"),
                visitor: findLeader(leaders.visitor, "QB")
            },
            {
                type: "RB",
                label: "Corredor",
                local: findLeader(leaders.local, "RB"),
                visitor: findLeader(leaders.visitor, "RB")
            },
            {
                type: "WR",
                label: "Receptor",
                local: findLeader(leaders.local, "WR"),
                visitor: findLeader(leaders.visitor, "WR")
            }
        ]
        : [];

    /*
        Si jugadores.json no contiene la imagen, intentar obtenerla
        desde el archivo individual del jugador, como en la web.
    */
    const photoKey = Array.from(
        new Set(
            rows.flatMap(function (row) {
                return [
                    row.local?.id,
                    row.visitor?.id
                ].filter(Boolean).map(String);
            })
        )
    ).sort().join("|");

    const [photos, setPhotos] =
        useState<Record<string, string>>({});

    useEffect(function () {
        let active = true;

        const ids = photoKey
            ? photoKey.split("|")
            : [];

        const missingIds = ids.filter(
            id => !players[id]?.img
        );

        if (missingIds.length === 0) {
            setPhotos({});

            return function () {
                active = false;
            };
        }

        Promise.all(
            missingIds.map(async function (id) {
                try {
                    const playerDetail = await getPlayerDetail(id);

                    return {
                        id,
                        image: playerDetail.Info?.img || ""
                    };
                } catch (error) {
                    console.warn(
                        "No fue posible cargar la imagen del jugador:",
                        id,
                        error
                    );

                    return { id, image: "" };
                }
            })
        ).then(function (results) {
            if (!active) {
                return;
            }

            const images: Record<string, string> = {};

            results.forEach(function (result) {
                if (result.image) {
                    images[result.id] = result.image;
                }
            });

            setPhotos(images);
        });

        return function () {
            active = false;
        };
    }, [photoKey, players]);

    const quickStats = useMemo(function () {
        if (!hasGamePlays) {
            return null;
        }

        return {
            local: calculateQuickTeamStats(
                plays,
                game.id_local
            ),
            visitor: calculateQuickTeamStats(
                plays,
                game.id_visita
            )
        };
    }, [
        plays,
        hasGamePlays,
        game.id_local,
        game.id_visita
    ]);

    return (
        <>
            <section className="game-section">
                <div className="game-section__heading">
                    <div>
                        <span>
                            {hasGamePlays
                                ? "PARTIDO"
                                : "TEMPORADA"}
                        </span>

                        <h2>
                            {hasGamePlays
                                ? "Líderes del partido"
                                : "Líderes de temporada"}
                        </h2>
                    </div>

                    <IonIcon icon={peopleOutline} />
                </div>

                <div className="game-leaders">
                    {!hasGamePlays && seasonLoading && (
                        <IonProgressBar />
                    )}

                    {!hasGamePlays && seasonError && (
                        <p
                            className="game-leaders__message"
                            role="status"
                        >
                            {seasonError}
                        </p>
                    )}

                    {leaders && (
                        <>
                            <div className="game-leaders__teams">
                                <span>
                                    {game.nombre_local || "Local"}
                                </span>

                                <span>
                                    {game.nombre_visita || "Visitante"}
                                </span>
                            </div>

                            {rows.map(function (row) {
                                return (
                                    <div
                                        className="game-leaders__row"
                                        key={row.type}
                                    >
                                        <LeaderPlayer
                                            player={row.local}
                                            type={row.type}
                                            players={players}
                                            photos={photos}
                                        />

                                        <div className="game-leaders__position">
                                            <strong>{row.type}</strong>
                                            <span>{row.label}</span>
                                        </div>

                                        <LeaderPlayer
                                            player={row.visitor}
                                            type={row.type}
                                            players={players}
                                            photos={photos}
                                        />
                                    </div>
                                );
                            })}

                            {!hasGamePlays && (
                                <p className="game-leaders__note">
                                    Se consideran partidos previos de la
                                    competencia, en fase de grupos.
                                </p>
                            )}
                        </>
                    )}

                    <IonButton
                        className="game-highlights__link"
                        fill="clear"
                        expand="block"
                        onClick={function () {
                            history.push(`/gamePlayers/${game.id}`);
                        }}
                    >
                        Ver estadísticas de jugadores
                        <IonIcon
                            slot="end"
                            icon={chevronForwardOutline}
                        />
                    </IonButton>
                </div>
            </section>

            {quickStats && (
                <section className="game-section">
                    <div className="game-section__heading">
                        <div>
                            <span>COMPARATIVA</span>
                            <h2>Estadísticas rápidas</h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <div className="game-quick-stats">
                        <div className="game-quick-stats__teams">
                            <span>
                                {game.nombre_local || "Local"}
                            </span>

                            <span>
                                {game.nombre_visita || "Visitante"}
                            </span>
                        </div>

                        <QuickRow
                            label="Yardas por aire"
                            localValue={quickStats.local.pass}
                            visitorValue={quickStats.visitor.pass}
                        />

                        <QuickRow
                            label="Yardas por tierra"
                            localValue={quickStats.local.rush}
                            visitorValue={quickStats.visitor.rush}
                        />

                        <QuickRow
                            label="Balones recuperados"
                            localValue={
                                quickStats.local.int +
                                quickStats.local.fr
                            }
                            visitorValue={
                                quickStats.visitor.int +
                                quickStats.visitor.fr
                            }
                        />

                        <IonButton
                            className="game-highlights__link"
                            fill="clear"
                            expand="block"
                            onClick={function () {
                                history.push(`/gameStats/${game.id}`);
                            }}
                        >
                            Ver estadísticas completas
                            <IonIcon
                                slot="end"
                                icon={chevronForwardOutline}
                            />
                        </IonButton>
                    </div>
                </section>
            )}
        </>
    );
};

export default GameHighlights;
